import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  translations,
  getTranslation,
  Language,
  Translations,
} from "@/data/translations";
import { trackLanguageChange } from "@/utils/analytics";
import { isUrlPrefixLanguage } from "@/constants/urlLangPrefix";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  getLanguageLabel: (lang: Language) => string;
  t: (key: string) => string;
  translations: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

interface LanguageProviderProps {
  children: ReactNode;
  initialLanguage?: Language; // Allow passing initial language for SSR
}

/** Only `/de/...` and `/fr/...` set language from the URL. Unprefixed paths (`/book`, `/`, …) return `null` so `preferred-language` in localStorage applies. */
const getLanguageFromPath = (pathname: string): Language | null => {
  const pathParts = pathname.split("/").filter(Boolean);
  const langCode = pathParts[0];
  return isUrlPrefixLanguage(langCode) ? (langCode as Language) : null;
};

// Helper function to get localized path
const getLocalizedPath = (currentPath: string, newLang: Language): string => {
  const pathParts = currentPath.split("/").filter(Boolean);

  // Check if first segment is a language code
  const isLangPath = isUrlPrefixLanguage(pathParts[0]);

  // Remove language prefix if it exists
  const pathWithoutLang = isLangPath
    ? "/" + pathParts.slice(1).join("/")
    : currentPath;

  // Add new language prefix if not English
  if (newLang === "en") {
    return pathWithoutLang || "/";
  } else {
    return "/" + newLang + pathWithoutLang;
  }
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  initialLanguage,
}) => {
  // Detect language synchronously during initialization (not in useEffect)
  const getInitialLanguage = (): Language => {
    if (initialLanguage) return initialLanguage;

    // Skip during SSR
    if (typeof window === "undefined") return "en";

    const pathLang = getLanguageFromPath(window.location.pathname);

    // Only allow languages with full content translations
    const availableLanguages = ["en", "de", "fr"];

    if (pathLang !== null && availableLanguages.includes(pathLang)) {
      return pathLang;
    }

    // Unprefixed URL: use saved preference (e.g. `/book` after choosing DE on another page)
    const savedLang = localStorage.getItem("preferred-language") as Language;
    if (savedLang && availableLanguages.includes(savedLang)) {
      return savedLang;
    }

    return "en";
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage());

  // Sync language to localStorage when it changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem("preferred-language", language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    const previousLanguage = language;
    setLanguageState(lang);

    // Skip during SSR
    if (typeof window === "undefined") return;

    localStorage.setItem("preferred-language", lang);

    // Track language change in analytics
    if (previousLanguage !== lang) {
      trackLanguageChange(previousLanguage, lang);
    }

    // Update URL path without page reload
    const newPath = getLocalizedPath(window.location.pathname, lang);
    window.history.pushState(
      {},
      "",
      newPath + window.location.search + window.location.hash,
    );

    // Trigger a popstate event to update the router
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const getLanguageLabel = (lang: Language): string => {
    const labels: Record<Language, string> = {
      en: "EN",
      de: "DE",
      fr: "FR",
      "zh-CN": "中文",
      ko: "한국어",
    };
    return labels[lang];
  };

  const t = (key: string): string => {
    return getTranslation(language, key);
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        getLanguageLabel,
        t,
        translations: translations[language],
      }}
      data-oid="ixvejyc"
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export type { Language } from "@/data/translations";
